import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiRequest } from "src/async/apiUtils";
import Toggle from "src/components/Shared/Forms/Toggles/Toggle";
import Preloader from "src/components/Shared/LoadingAnimations/Preloader";
import PreloaderIcon from "src/components/Shared/LoadingAnimations/PreloaderIcon";
import useFetch from "src/hooks/useFetch";

const EnvironmentSetup = ({ client }) => {
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [enabledProviders, setEnabledProviders] = useState({
    domo: false,
    quicksight: false,
    metabase: false,
  });

  const {
    response: { data: providers },
    status: { fetching },
    refreshData: refresProviders,
  } = useFetch(`/clients/:client_id/data-providers`, { params: { client_id: client?._id } });

  const handleToggle = async (provider) => {
    try {
      const newStatus = !enabledProviders[provider?._id];
      setLoadingProvider(provider._id);

      await apiRequest("POST", `/clients/:client_id/data-providers/:provider_id/status`, {
        params: {
          client_id: client?._id,
          provider_id: provider?._id,
        },
        body: {
          status: newStatus,
        },
      });

      setEnabledProviders((prev) => ({
        ...prev,
        [provider._id]: newStatus,
      }));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingProvider(null);
    }
  };

  useEffect(() => {
    refresProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?._id]);

  useEffect(() => {
    if (providers?.length) {
      const mapped = {};
      providers.forEach((p) => {
        mapped[p._id] = p.status;
      });
      setEnabledProviders(mapped);
    }
  }, [providers]);

  if (fetching) {
    return (
      <div className="relative">
        <Preloader color="text-gray-400 fill-gray-700" />
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="mb-3 border-b pb-3">
        <h2 className="text-lg font-bold text-gray-900">Environment Setup</h2>
        <p className="text-sm text-gray-600">Control data provider availability in the client’s environment.</p>
      </div>

      <div className="space-y-3">
        {providers.map((provider) => (
          <div
            key={provider?._id}
            className="flex items-center justify-between rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50">
            <div className="flex items-center space-x-4">
              <div className="h-8 w-8 overflow-hidden rounded-md">
                <div dangerouslySetInnerHTML={{ __html: provider?.image }} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{provider?.name}</h3>
                <p className="text-sm text-gray-500">{provider?.description}</p>
              </div>
            </div>

            {loadingProvider === provider._id ? (
              <PreloaderIcon
                height="h-6"
                width="w-6"
              />
            ) : (
              <Toggle
                checked={enabledProviders[provider._id]}
                onChange={() => handleToggle(provider)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default EnvironmentSetup;
